import { useCallback, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import FieldHeader from "./components/FieldHeader";
import FieldBottomNav from "./components/FieldBottomNav";
import OfflineBanner from "./components/OfflineBanner";
import FieldHome from "./components/FieldHome";
import FieldMap from "./components/FieldMap";
import FieldQrScanner from "./components/FieldQrScanner";
import FieldTreeSearch from "./components/FieldTreeSearch";
import { useFieldGps, useOnlineStatus } from "./useFieldGps";
import { NoRealData } from "@/components/shared";
import { useCampaignsList } from "@/hooks";
import { isAuthenticated, loginPath } from "@/lib/auth";
import type { CampaignSummary } from "@/types/campaign";

type Screen = "home" | "nova-arvore" | "vistoria" | "fotos" | "mapa" | "rascunhos" | "sincronizacao" | "qrcode" | "exemplar";

const pathToScreen: Record<string, Screen> = {
  "/coletor": "home",
  "/coletor/nova-arvore": "nova-arvore",
  "/coletor/vistoria": "vistoria",
  "/coletor/fotos": "fotos",
  "/coletor/mapa": "mapa",
  "/coletor/rascunhos": "rascunhos",
  "/coletor/sincronizacao": "sincronizacao",
  "/coletor/qrcode": "qrcode",
  "/coletor/exemplar": "exemplar",
};

/** Funcoes que dependem do modo offline (fila local), ainda nao implementado no PWA. */
const OFFLINE_PENDING: Partial<Record<Screen, { title: string; description: string; icon: string }>> = {
  fotos: {
    title: "Fotos pendentes",
    description:
      "Ainda não há fila de fotos no aparelho: anexe as fotos na própria vistoria do exemplar, com conexão. A fila offline chega na próxima etapa do coletor.",
    icon: "ri-camera-line",
  },
  rascunhos: {
    title: "Rascunhos locais",
    description:
      "As vistorias em rascunho ficam no servidor e aparecem na ficha do exemplar e em Vistorias. Rascunhos guardados no aparelho chegam com o modo offline.",
    icon: "ri-draft-line",
  },
  sincronizacao: {
    title: "Sincronização",
    description:
      "Tudo o que é gravado pelo coletor vai direto ao servidor, então não há fila a sincronizar. A fila de envio chega com o modo offline.",
    icon: "ri-cloud-line",
  },
};

function BackHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <button onClick={onBack} className="w-8 h-8 rounded-lg hover:bg-[#E8EAED] flex items-center justify-center" aria-label="Voltar">
        <i className="ri-arrow-left-line text-[#3F4A52] text-lg" />
      </button>
      <h2 className="text-lg font-heading font-bold text-[#3F4A52]">{title}</h2>
    </div>
  );
}

function pickActiveMission(campaigns: CampaignSummary[]): CampaignSummary | null {
  return campaigns.find((c) => c.status === "EM_ANDAMENTO") ?? null;
}

function ColetorContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const online = useOnlineStatus();
  const gps = useFieldGps();
  const { campaigns, loading: loadingCampaigns, error: campaignsError } = useCampaignsList();
  const mission = useMemo(() => pickActiveMission(campaigns), [campaigns]);

  const currentScreen: Screen = pathToScreen[location.pathname] || "home";
  const goTo = useCallback((path: string) => navigate(path), [navigate]);
  const goHome = useCallback(() => navigate("/coletor"), [navigate]);

  useEffect(() => {
    document.title = "FloraIQ — Coletor de Campo";
  }, []);

  // Cadastro real do exemplar e o FIQ-1 do site; leva a posicao do GPS quando houver.
  const newTreeHref = useMemo(() => {
    const params = new URLSearchParams();
    if (gps.latitude !== null && gps.longitude !== null) {
      params.set("lat", gps.latitude.toFixed(7));
      params.set("lng", gps.longitude.toFixed(7));
    }
    if (mission) params.set("project_id", String(mission.projectId));
    const qs = params.toString();
    return qs ? `/cta/novo?${qs}` : "/cta/novo";
  }, [gps.latitude, gps.longitude, mission]);

  const bottomNavItems = useMemo(
    () => [
      { icon: "ri-home-line", label: "Início", href: "/coletor", active: currentScreen === "home" },
      { icon: "ri-leaf-line", label: "Nova Árvore", href: "/coletor/nova-arvore", active: currentScreen === "nova-arvore" },
      { icon: "ri-qr-scan-2-line", label: "QR Code", href: "/coletor/qrcode", active: currentScreen === "qrcode" },
      { icon: "ri-map-pin-line", label: "Próximos", href: "/coletor/mapa", active: currentScreen === "mapa" },
    ],
    [currentScreen],
  );

  const missionLabel = loadingCampaigns
    ? "Carregando missão..."
    : mission
      ? mission.name
      : "Nenhuma missão em andamento";

  const mobileOnly = OFFLINE_PENDING[currentScreen];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <FieldHeader
        projetoNome={missionLabel}
        connectionStatus={online ? "online" : "offline"}
        gpsStatus={gps.status}
        precisaoGps={gps.accuracy ?? undefined}
      />
      <OfflineBanner visible={!online} />

      <div className="pb-16">
        {currentScreen === "home" && (
          <FieldHome
            mission={mission}
            missionLoading={loadingCampaigns}
            missionError={campaignsError}
            gps={gps}
            newTreeHref={newTreeHref}
            onNavigate={goTo}
          />
        )}

        {currentScreen === "nova-arvore" && (
          <div className="px-4 pt-4 pb-20 max-w-lg md:max-w-2xl mx-auto space-y-4">
            <BackHeader title="Nova árvore" onBack={goHome} />
            <p className="text-sm text-[#3F4A52]/80">
              O cadastro do exemplar arbóreo pelo site é feito no formulário FIQ-1, que verifica duplicidade, deriva bairro,
              quadra e logradouro pela posição e grava a proveniência da localização.
            </p>
            {gps.latitude !== null && gps.longitude !== null && (
              <p className="text-xs text-[#3F4A52]/70">
                A posição atual do aparelho ({gps.latitude.toFixed(6)}, {gps.longitude.toFixed(6)}
                {gps.accuracy !== null ? `, ±${Math.round(gps.accuracy)} m` : ""}) será levada ao formulário.
              </p>
            )}
            <Link
              to={newTreeHref}
              className="block w-full text-center py-4 rounded-xl bg-[#1B6E4F] text-white font-heading font-bold text-sm"
            >
              Abrir cadastro do exemplar
            </Link>
          </div>
        )}

        {currentScreen === "vistoria" && (
          <div className="px-4 pt-4 pb-20 max-w-lg md:max-w-2xl mx-auto space-y-4">
            <BackHeader title="Nova vistoria" onBack={goHome} />
            <p className="text-sm text-[#3F4A52]/80">
              A vistoria nasce pela ficha do exemplar arbóreo. Localize o exemplar pelos exemplares próximos ou pela plaqueta (QR Code) e toque em “Nova vistoria”.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => goTo("/coletor/mapa")}
                className="py-3 rounded-xl border border-[#E8EAED] bg-white text-sm font-medium text-[#3F4A52]"
              >
                Exemplares próximos
              </button>
              <button
                onClick={() => goTo("/coletor/qrcode")}
                className="py-3 rounded-xl bg-[#0052CC] text-white text-center font-heading font-bold text-sm"
              >
                Ler QR Code
              </button>
            </div>
          </div>
        )}

        {currentScreen === "mapa" && <FieldMap gps={gps} projectId={mission?.projectId} onClose={goHome} />}

        {currentScreen === "qrcode" && (
          <div className="px-4 pt-4 pb-20 max-w-lg md:max-w-xl mx-auto">
            <BackHeader title="Ler QR Code" onBack={goHome} />
            <FieldQrScanner />
          </div>
        )}

        {currentScreen === "exemplar" && (
          <div className="px-4 pt-4 pb-20 max-w-lg md:max-w-2xl mx-auto">
            <BackHeader title="Fotos de exemplar cadastrado" onBack={goHome} />
            <FieldTreeSearch projectId={mission?.projectId} />
          </div>
        )}

        {mobileOnly && (
          <div className="px-4 pt-4 pb-20 max-w-lg md:max-w-2xl mx-auto">
            <BackHeader title={mobileOnly.title} onBack={goHome} />
            <NoRealData title={mobileOnly.title} description={mobileOnly.description} icon={mobileOnly.icon} />
          </div>
        )}
      </div>

      <FieldBottomNav items={bottomNavItems} onNavigate={goTo} />
    </div>
  );
}

export default function ColetorPage() {
  const location = useLocation();
  // Sem sessao, qualquer consulta redirecionaria ao login no meio da tela.
  if (!isAuthenticated()) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-4 text-center">
          <NoRealData
            title="Coletor de Campo"
            description="Entre com sua matrícula para consultar a missão ativa e os exemplares próximos."
            icon="ri-login-box-line"
            variant="page"
          />
          <Link to={loginPath(location.pathname)} className="inline-block px-5 py-2.5 rounded-xl bg-[#1B6E4F] text-white font-heading font-bold text-sm">
            Entrar
          </Link>
        </div>
      </div>
    );
  }
  return <ColetorContent />;
}
